import {
	BankOutlined,
	CloudSyncOutlined,
	LockOutlined,
	UserOutlined,
} from "@ant-design/icons";
import dayjs from 'dayjs';
import {
	Alert,
	Button,
	Col,
	DatePicker,
	Form,
	Input,
	InputNumber,
	Modal,
	Row,
	Select,
	Spin,
	Table,
	Tag,
	Checkbox
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import db from "../../db/database";
import { getAllAperturasDeCaja } from "../../services/aperturaDeCajaService";
import { getAperturaActiva } from "../../services/cajaEstadoService";
import { getCajaByNoOrId } from "../../services/cajaService";
import {
	addCierreDeCaja,
	addCierreDetalle,
	updateCierreDeCaja,
} from "../../services/cierreDeCajaService";
import {
	getPaymentLinesByDate,
	getPaymentLinesByDateTimeRange,
} from "../../services/invoicePaymentService";
import { syncAll } from "../../services/syncService";
import { getTPVByNoOrId } from "../../services/tpvService";
import { getAllUsers } from "../../services/userService";
import DenominacionesCalculator from "../common/DenominacionesCalculator";
import "./CierreDeCajaStyles.css";
import CierreTicket from "./CierreTicket";
import { useConfig } from "../../contexts/ConfigContext";
import { formatNumberByCountry, parseNumberByCountry } from "../../utils/formatNumbers";
import { COUNTRY_CODES } from "../../constants/countries";

const { Option } = Select;

const CierreDeCajaForm = ({ cierre, onSave, onCancel }) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [tempDetalles, setTempDetalles] = useState([]); // Para detalles antes de guardar
	const [error, setError] = useState("");
	const [saveButtonText, setSaveButtonText] = useState("Guardar");
	const [users, setUsers] = useState([]);
	const [ultimaApertura, setUltimaApertura] = useState(null);
	const [pagosPorFecha, setPagosPorFecha] = useState([]);
	const [aperturaActiva, setAperturaActiva] = useState(null);
	const [aperturaTexto, setAperturaTexto] = useState("");
	const [syncingModal, setSyncingModal] = useState(false);
	const [syncingStatus, setSyncingStatus] = useState("");
	const [arqueoEfectivoUS, setArqueoEfectivoUS] = useState(0);
	const [arqueoTarjetaUS, setArqueoTarjetaUS] = useState(0);
	const [adminAuthModal, setAdminAuthModal] = useState(false);
	const [adminForm] = Form.useForm();
	const [adminAuthLoading, setAdminAuthLoading] = useState(false);
	const [pendingValues, setPendingValues] = useState(null);
	const [ticketVisible, setTicketVisible] = useState(false);
	const [savedCierreForTicket, setSavedCierreForTicket] = useState(null);
	const [cajaInfo, setCajaInfo] = useState(null);
	const [tpvInfo, setTpvInfo] = useState(null);
	const [confirmCierreEnCeroModal, setConfirmCierreEnCeroModal] = useState(false);
	const [pendingCierreValues, setPendingCierreValues] = useState(null);
	const [cierreHistorico, setCierreHistorico] = useState(false);
	const selectedStartDate = Form.useWatch("FechaDeApertura", form);
	const cierreHistoricoChecked = Form.useWatch("CierreHistorico", form);
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 5,
	});
	const { currentUser } = useAuth();
	const { country } = useConfig();

	const importeApertura = Form.useWatch("ImporteDeApertura", form) ?? 0;
	const arqueoEfectivo = Form.useWatch("ArqueoEfectivoUS", form) ?? 0;
	const arqueoTarjeta = Form.useWatch("ArqueoTarjetaUS", form) ?? 0;



	// Utilidades para trabajar en centavos y evitar errores de coma flotante
	const toCents = (value) => {
		const safe = value ?? 0;
		return Math.round((safe + Number.EPSILON) * 100);
	};

	const fromCents = (cents) => cents / 100;

	const isEditing = !!cierre?.id;

	useEffect(() => {
		// Cargar usuarios y la última apertura de caja
		const cargarDatos = async () => {
			try {
				setLoading(true);

				// Obtener todos los usuarios
				const usuariosData = await getAllUsers();
				setUsers(usuariosData);

				// Si está en modo histórico y el usuario eligió fecha
				if (cierreHistoricoChecked && selectedStartDate) {
					const aperturaDateTime = dayjs(selectedStartDate)
						.startOf("day")
						.toDate();

					console.log("Cargando histórico desde:", aperturaDateTime);

					// Fecha de cierre = última hora de esa fecha
					const cierreDateTime = dayjs(selectedStartDate)
						.endOf("day")
						.toDate();

					await cargarPagosPorRangoFechaHora(aperturaDateTime, cierreDateTime, cierreHistoricoChecked);
					return;
				}

				// Obtener información sobre la caja actualmente abierta (cuando NO es histórico)
				const cajaActiva = await getAperturaActiva();
				setAperturaActiva(cajaActiva);

				if (cajaActiva) {
					setAperturaTexto(
						cajaActiva.id
							? `${cajaActiva.id} - ${new Date(cajaActiva.FechaDeApertura)
								.toISOString()
								.split("T")[0]
							}`
							: "Pendiente"
					);

					// Si hay una caja activa, usarla como apertura relacionada
					if (cajaActiva.FechaDeApertura && cajaActiva.HoraDeApertura) {
						const fechaApertura = dayjs(cajaActiva.FechaDeApertura).format("YYYY-MM-DD");
						const horaApertura = cajaActiva.HoraDeApertura || "00:00";
						const aperturaDateTime = dayjs(
							`${fechaApertura} ${horaApertura}`,
							"YYYY-MM-DD HH:mm"
						).toDate();

						console.log(
							`📅 Fecha/Hora de apertura: ${fechaApertura} ${horaApertura}`
						);
						console.log(`🕒 DateTime de apertura: ${aperturaDateTime}`);

						// Usar la nueva función que considera el rango de fecha y hora
						await cargarPagosPorRangoFechaHora(aperturaDateTime, null, cierreHistoricoChecked);
					} else if (cajaActiva.FechaDeApertura) {
						// Fallback: usar solo fecha si no hay hora
						await cargarPagosPorFecha(cajaActiva.FechaDeApertura);
					}
				} else {
					// Si no hay caja activa pero hay aperturas previas
					// Ordenar por fecha en orden descendente
					const aperturas = await getAllAperturasDeCaja();
					if (aperturas && aperturas.length > 0) {
						const ultimaAperturaData = aperturas.sort(
							(a, b) => new Date(b.FechaDeApertura) - new Date(a.FechaDeApertura)
						)[0];
						setUltimaApertura(ultimaAperturaData);

						// Si hay una última apertura, cargar los pagos con fecha y hora si están disponibles
						if (ultimaAperturaData.HoraDeApertura) {
							const fechaApertura = dayjs(
								ultimaAperturaData.FechaDeApertura
							).format("YYYY-MM-DD");
							const horaApertura = ultimaAperturaData.HoraDeApertura || "00:00";
							const aperturaDateTime = dayjs(
								`${fechaApertura} ${horaApertura}`,
								"YYYY-MM-DD HH:mm"
							).toDate();

							console.log(
								`📅 Fecha/Hora de última apertura: ${fechaApertura} ${horaApertura}`
							);

							// Usar la nueva función que considera el rango de fecha y hora
							await cargarPagosPorRangoFechaHora(aperturaDateTime, null, cierreHistoricoChecked);
						} else {
							// Fallback: usar solo fecha si no hay hora
							await cargarPagosPorFecha(ultimaAperturaData.FechaDeApertura);
						}
					}
				}
			} catch (error) {
				console.error("Error al cargar datos iniciales:", error);
				window.messageApi.error("Error al cargar datos necesarios");
			} finally {
				setLoading(false);
			}
		};

		cargarDatos();
	}, [selectedStartDate, cierreHistoricoChecked]);

	const cargarPagosPorFecha = async (fecha) => {
		try {
			const pagos = await getPaymentLinesByDate(fecha);
			setPagosPorFecha(pagos);
		} catch (error) {
			console.error("Error al cargar pagos por fecha:", error);
			window.messageApi.error(
				"No se pudieron cargar los pagos de facturas para la fecha seleccionada"
			);
		}
	};

	// Nueva función para cargar pagos por rango de fecha y hora
	const cargarPagosPorRangoFechaHora = async (
		aperturaDateTime,
		cierreDateTime = null,
		historicos = false
	) => {
		try {
			console.log("🔄 Cargando pagos por rango de fecha y hora...");
			const pagos = await getPaymentLinesByDateTimeRange(
				aperturaDateTime,
				cierreDateTime,
				historicos
			);
			setPagosPorFecha(pagos);
			console.log(`✅ Pagos cargados: ${pagos.length} líneas de pago`);
		} catch (error) {
			console.error("Error al cargar pagos por rango de fecha y hora:", error);
			window.messageApi.error(
				"No se pudieron cargar los pagos de facturas para el rango de tiempo seleccionado"
			);
		}
	};

	useEffect(() => {
		// Verificar si hay una caja abierta (si no estamos editando)
		if (!isEditing && !aperturaActiva) {
			setError(
				"No hay una caja abierta actualmente. Debe abrir una caja antes de realizar un cierre."
			);
		} else {
			setError("");
		}

		// Si estamos editando un cierre existente, cargar sus datos
		if (isEditing && cierre) {
			setSaveButtonText(
				cierre.Estado === "Cerrado" ? "Cerrar caja" : "Actualizar cierre"
			);

			setArqueoEfectivoUS(cierre.ArqueoEfectivoUS || 0);
			setArqueoTarjetaUS(cierre.ArqueoTarjetaUS || 0);

			form.setFieldsValue({
				Cajero: cierre.Cajero,
				ImporteDeApertura: cierre.ImporteDeApertura,
				FechaDeApertura: cierre.FechaDeApertura
					? dayjs(cierre.FechaDeApertura)
					: dayjs(),
				ImporteDeCierreUS: cierre.ImporteDeCierreUS,
				ArqueoEfectivoUS: cierre.ArqueoEfectivoUS,
				ArqueoTarjetaUS: cierre.ArqueoTarjetaUS,
				FechaDeCierre: cierre.FechaDeCierre
					? dayjs(cierre.FechaDeCierre)
					: null,
				Estado: cierre.Estado || "Abierto",
				idApertura: cierre.idApertura,
				Comprobante_Tarjetas: cierre.Comprobante_Tarjetas || "",
			});
			// Cargar pagos para la fecha de apertura
			// En modo edición, usar el rango desde apertura hasta cierre
			if (cierre.FechaDeApertura) {
				if (aperturaActiva && aperturaActiva.HoraDeApertura) {
					// Si hay apertura activa con hora, usar rango de fecha y hora
					const fechaApertura = dayjs(cierre.FechaDeApertura).format(
						"YYYY-MM-DD"
					);
					const horaApertura = aperturaActiva.HoraDeApertura || "00:00";
					const aperturaDateTime = dayjs(
						`${fechaApertura} ${horaApertura}`,
						"YYYY-MM-DD HH:mm"
					).toDate();

					// Para edición, usar hasta la fecha de cierre si existe
					let cierreDateTime = null;
					if (cierre.FechaDeCierre) {
						cierreDateTime = dayjs(cierre.FechaDeCierre).endOf("day").toDate();
					}

					console.log(
						`📝 Editando cierre - Rango: ${aperturaDateTime} hasta ${cierreDateTime || "ahora"
						}`
					);
					cargarPagosPorRangoFechaHora(aperturaDateTime, cierreDateTime, cierreHistoricoChecked);
				} else {
					// Fallback: usar solo fecha
					cargarPagosPorFecha(cierre.FechaDeApertura);
				}
			}
			// Limpiar detalles temporales si estamos editando
			setTempDetalles([]);
		} else {
			// Si es un nuevo cierre, establecer valores por defecto basados en la caja activa
			if (aperturaActiva) {
				form.setFieldsValue({
					Cajero: aperturaActiva.Cajero,
					FechaDeApertura: aperturaActiva.FechaDeApertura
						? dayjs(aperturaActiva.FechaDeApertura)
						: dayjs(),
					ImporteDeApertura: aperturaActiva.ImporteDeApertura || 0,
					Estado: "Cerrado", // Siempre cerrado para nuevos cierres
					FechaDeCierre: dayjs(), // Fecha actual
					ImporteDeCierreUS: 0, // Se calculará automáticamente
					ArqueoEfectivoUS: 0,
					ArqueoTarjetaUS: 0,
					idApertura: aperturaActiva.id, // No (número) de la apertura relacionada
					Comprobante_Tarjetas: "",
				});
			} else if (ultimaApertura) {
				// Si no hay apertura activa, usar datos de la última apertura como referencia
				form.setFieldsValue({
					Cajero: ultimaApertura.Cajero,
					FechaDeApertura: ultimaApertura.FechaDeApertura
						? dayjs(ultimaApertura.FechaDeApertura)
						: dayjs(),
					ImporteDeApertura: ultimaApertura.ImporteDeApertura || 0,
					Estado: "Cerrado", // Siempre cerrado para nuevos cierres
					FechaDeCierre: dayjs(), // Fecha actual
					ImporteDeCierreUS: 0, // Se calculará automáticamente
					ArqueoEfectivoUS: 0,
					ArqueoTarjetaUS: 0,
					idApertura: ultimaApertura.id, // No (número) de la apertura relacionada
					Comprobante_Tarjetas: "",
				});
			} else {
				// Si no hay apertura previa, establecer valores por defecto básicos
				form.setFieldsValue({
					Cajero: currentUser?.No,
					FechaDeApertura: dayjs(),
					Estado: "Cerrado",
					FechaDeCierre: dayjs(),
					ImporteDeApertura: 0,
					ImporteDeCierreUS: 0, // Se calculará automáticamente
					ArqueoEfectivoUS: 0,
					ArqueoTarjetaUS: 0,
					Comprobante_Tarjetas: "",
				});
			}
		}
	}, [form, cierre, isEditing, ultimaApertura, currentUser, aperturaActiva]);

	const calculatePagosTotal = useCallback(() => {
		// Calcular total de todos los pagos de facturas en centavos
		const totalCents = pagosPorFecha.reduce(
			(sum, pago) => sum + toCents(pago.Amount || 0),
			0
		);
		return fromCents(totalCents);
	}, [pagosPorFecha]);

	// Calcular totales por tipo de pago separando facturas y notas de crédito
	const calculateTotalsByType = useCallback(() => {
		// Facturas (valores positivos) en centavos
		const facturasEfectivoCents = pagosPorFecha
			.filter((pago) => pago.Tipo_Pago === "Efectivo" && (pago.Amount || 0) > 0)
			.reduce((sum, pago) => sum + toCents(pago.Amount || 0), 0);

		const facturasTarjetaCents = pagosPorFecha
			.filter((pago) => pago.Tipo_Pago === "Tarjeta" && (pago.Amount || 0) > 0)
			.reduce((sum, pago) => sum + toCents(pago.Amount || 0), 0);

		// Notas de crédito (valores negativos) en centavos
		const notasCreditoEfectivoCents = pagosPorFecha
			.filter((pago) => pago.Tipo_Pago === "Efectivo" && (pago.Amount || 0) < 0)
			.reduce((sum, pago) => sum + Math.abs(toCents(pago.Amount || 0)), 0);

		const notasCreditoTarjetaCents = pagosPorFecha
			.filter((pago) => pago.Tipo_Pago === "Tarjeta" && (pago.Amount || 0) < 0)
			.reduce((sum, pago) => sum + Math.abs(toCents(pago.Amount || 0)), 0);

		// Totales generales en centavos
		const totalEfectivoCents = facturasEfectivoCents - notasCreditoEfectivoCents;
		const totalTarjetaCents = facturasTarjetaCents - notasCreditoTarjetaCents;

		return {
			facturasEfectivo: fromCents(facturasEfectivoCents),
			facturasTarjeta: fromCents(facturasTarjetaCents),
			notasCreditoEfectivo: fromCents(notasCreditoEfectivoCents),
			notasCreditoTarjeta: fromCents(notasCreditoTarjetaCents),
			totalEfectivo: fromCents(totalEfectivoCents),
			totalTarjeta: fromCents(totalTarjetaCents),
			total: fromCents(totalEfectivoCents + totalTarjetaCents),
		};
	}, [pagosPorFecha]);

	const totals = calculateTotalsByType(pagosPorFecha);

	const totalEfectivoSistema = totals.totalEfectivo;
	const totalEsperadoEfectivo = (importeApertura + totalEfectivoSistema);

	const diferencia = totalEsperadoEfectivo - arqueoEfectivo;

	// Efecto para actualizar automáticamente ImporteDeCierreUS cuando cambian los pagos
	useEffect(() => {
		// Solo actualizar si no estamos en modo edición o si estamos creando un nuevo cierre
		if (!isEditing || !cierre?.id) {
			const totalPagos = calculatePagosTotal();
			form.setFieldsValue({
				ImporteDeCierreUS: totalPagos,
			});
		}
	}, [pagosPorFecha, isEditing, cierre, form, calculatePagosTotal]);

	const handleArqueoEfectivoUSChange = (nuevoArqueo) => {
		setArqueoEfectivoUS(nuevoArqueo);
		// También actualizar el formulario
		form.setFieldsValue({ ArqueoEfectivoUS: nuevoArqueo });
	};

	const handleArqueoTarjetaUSChange = (nuevoArqueo) => {
		setArqueoTarjetaUS(nuevoArqueo);
		// También actualizar el formulario
		form.setFieldsValue({ ArqueoTarjetaUS: nuevoArqueo });
	};

	// Procesar los pagos en detalles para el cierre
	const procesarPagosComoDetalles = () => {
		if (!cierre?.No) {
			const idAperturaSeleccionada = form.getFieldValue("idApertura");
			// Convertir pagos a detalles temporales (se usarán después de guardar el cierre)
			const detallesTemporales = pagosPorFecha.map((pago, index) => ({
				id: `temp-${index}`,
				temp: true,
				idFormaPago: pago.Payment_Method_Code,
				DesFormaPago: pago.Payment_Method,
				MontoPago: pago.Amount,
				TipoPago: pago.Tipo_Pago || "Efectivo",
				idCierre: null,
				idApertura: idAperturaSeleccionada,
			}));

			setTempDetalles(detallesTemporales);
		}
	};

	// Función para validar credenciales de administrador
	const validateAdminCredentials = async (username, password) => {
		try {
			// Buscar el usuario en la base de datos por su No (igual que en loginUser)
			const user = await db.users
				.where("No")
				.equals(username)
				.and((user) => !user.deleted) // Asegurar que no está eliminado
				.first();

			if (!user) {
				return false; // Usuario no encontrado
			}

			// Verificar contraseña
			if (user.password !== password) {
				return false; // Contraseña incorrecta
			}

			// Verificar si es administrador
			if (!user.Admin) {
				return false; // No es administrador
			}

			return true; // Usuario válido y es administrador
		} catch (error) {
			console.error("Error al validar credenciales de administrador:", error);
			return false;
		}
	};

	// Función para manejar la autorización de administrador
	const handleAdminAuth = async (adminValues) => {
		try {
			setAdminAuthLoading(true);

			const { username, password } = adminValues;

			if (!validateAdminCredentials(username, password)) {
				window.messageApi.error("Credenciales de administrador incorrectas");
				return;
			}

			// Si las credenciales son correctas, continuar con el cierre
			window.messageApi.success("Autorización de administrador concedida");
			setAdminAuthModal(false);
			adminForm.resetFields();
			setError(""); // Limpiar el error anterior

			// Continuar con el guardado usando los valores pendientes
			await continuarConCierre(pendingValues);
		} catch (error) {
			console.error("Error en autorización de administrador:", error);
			window.messageApi.error("Error durante la autorización");
		} finally {
			setAdminAuthLoading(false);
		}
	};

	// Función para continuar con el cierre después de la autorización
	const continuarConCierre = async (formattedValues) => {
		try {
			setLoading(true);

			// Antes de guardar el cierre, procesar los pagos como detalles temporales
			if (!isEditing) {
				procesarPagosComoDetalles();
			}

			let savedCierre;
			if (isEditing) {
				// Actualizar cierre existente
				savedCierre = await updateCierreDeCaja(cierre.id, formattedValues);
				window.messageApi.success("Cierre de caja actualizado correctamente");

				// Para ediciones, notificar directamente al componente padre
				onSave(savedCierre);
				return; // Salir temprano, no mostrar ticket
			} else {
				// Crear nuevo cierre
				savedCierre = await addCierreDeCaja(formattedValues);
				window.messageApi.success("Cierre de caja creado correctamente");

				// Guardar detalles temporales si hay alguno
				if (tempDetalles.length > 0) {
					for (const tempDetalle of tempDetalles) {
						const { id, temp, ...detalleData } = tempDetalle;
						await addCierreDetalle({
							...detalleData,
							cierreId: savedCierre.id, // ID interno del cierre para la base de datos local
							idCierre: savedCierre.No || null, // No. del cierre para BC
							idApertura: form.getFieldValue("idApertura"),
							TipoPago: detalleData.TipoPago || "Efectivo",
						});
					}
					window.messageApi.success(
						`${tempDetalles.length} detalles guardados correctamente`
					);
				} else {
					// Guardar los pagos actuales como detalles si no hay detalles temporales
					for (const pago of pagosPorFecha) {
						await addCierreDetalle({
							cierreId: savedCierre.id, // ID interno del cierre para la base de datos local
							idCierre: savedCierre.No || null, // No. del cierre para BC (puede ser null si aún no está sincronizado)
							idApertura: form.getFieldValue("idApertura"),
							idFormaPago: pago.Payment_Method,
							DesFormaPago: pago.Payment_Method,
							MontoPago: pago.Amount,
							TipoPago: pago.Tipo_Pago || "Efectivo",
						});
					}
					window.messageApi.success(
						`${pagosPorFecha.length} líneas de pago guardadas correctamente`
					);
				}

				// Iniciar sincronización después de crear el cierre
				await handleSyncAfterCierre();
			}

			// Guardar los datos del cierre para el ticket
			setSavedCierreForTicket({
				...savedCierre,
				detalles:
					tempDetalles.length > 0
						? tempDetalles
						: pagosPorFecha.map((pago) => ({
							idFormaPago: pago.Payment_Method_Code,
							DesFormaPago: pago.Payment_Method,
							MontoPago: pago.Amount,
							TipoPago: pago.Tipo_Pago || "Efectivo",
						})),
			});

			// Mostrar el ticket de cierre como paso intermedio
			setTicketVisible(true);

			// NO notificar al componente padre aún - esperar a que cierre el ticket
		} catch (error) {
			console.error("Error al guardar cierre de caja:", error);
			setError(error.message || "Error al procesar el cierre de caja");
		} finally {
			setLoading(false);
		}
	};

	// Función para cancelar la autorización de administrador
	const handleCancelAdminAuth = () => {
		setAdminAuthModal(false);
		adminForm.resetFields();
		setPendingValues(null);
		setLoading(false);
	};

	// Función para confirmar el cierre en cero
	const handleConfirmCierreEnCero = async () => {
		try {
			setConfirmCierreEnCeroModal(false);
			setLoading(true);

			if (pendingCierreValues) {
				const { formattedValues, importeCierre, arqueoTotalActual } = pendingCierreValues;
				await procesarCierre(formattedValues, importeCierre, arqueoTotalActual);
			}
		} catch (error) {
			console.error("Error al procesar cierre en cero:", error);
			setError(error.message || "Error al procesar el cierre de caja");
		} finally {
			setPendingCierreValues(null);
			setLoading(false);
		}
	};

	// Función para cancelar el cierre en cero
	const handleCancelCierreEnCero = () => {
		setConfirmCierreEnCeroModal(false);
		setPendingCierreValues(null);
		setLoading(false);
	};

	// Función para manejar el cierre del ticket y completar el proceso
	const handleTicketClose = () => {
		setTicketVisible(false);
		const cierreGuardado = savedCierreForTicket;
		setSavedCierreForTicket(null);

		// Ahora sí notificar al componente padre para cerrar el formulario
		if (cierreGuardado) {
			onSave(cierreGuardado);
		}
	};

	// Función para manejar la sincronización después de crear el cierre
	const handleSyncAfterCierre = async () => {
		try {
			setSyncingModal(true);
			setSyncingStatus("Iniciando sincronización...");

			await syncAll();

			setSyncingStatus("Sincronización completada exitosamente");

			// Esperar un momento antes de cerrar el modal
			setTimeout(() => {
				setSyncingModal(false);
				setSyncingStatus("");
				window.messageApi.success("Cierre de caja sincronizado correctamente");
			}, 2000);
		} catch (error) {
			setSyncingStatus("Error durante la sincronización: " + error.message);
			console.error("Error durante la sincronización:", error);
			window.messageApi.error(
				"Error durante la sincronización: " + error.message
			);

			// Cerrar el modal después de mostrar el error
			setTimeout(() => {
				setSyncingModal(false);
				setSyncingStatus("");
			}, 3000);
		}
	};

	const handleSubmit = async (values) => {
		try {
			// Verificar si hay una caja abierta (si no estamos editando)
			if (!isEditing && !aperturaActiva) {
				setError(
					"No hay una caja abierta actualmente. Debe abrir una caja antes de realizar un cierre."
				);
				return;
			}

			setError("");
			setLoading(true);

			// Formatear fechas primero
			const formattedValues = {
				...values,
				ArqueoEfectivoUS: arqueoEfectivoUS, // Usar el valor del estado
				ArqueoTarjetaUS: arqueoTarjetaUS, // Usar el valor del estado
				FechaDeApertura: values.FechaDeApertura.format("YYYY-MM-DD"),
				FechaDeCierre: values.FechaDeCierre
					? values.FechaDeCierre.format("YYYY-MM-DD")
					: null,
			};

			const importeCierre = parseFloat(values.ImporteDeCierreUS) || 0;
			const arqueoTotalActual = parseFloat(arqueoEfectivoUS) + parseFloat(arqueoTarjetaUS);

			// Validación de seguridad para cierre en 0
			if (importeCierre === 0 && arqueoTotalActual === 0) {
				setPendingCierreValues({ formattedValues, importeCierre, arqueoTotalActual });
				setConfirmCierreEnCeroModal(true);
				setLoading(false);
				return;
			}

			// Si no es cierre en 0, proceder con la validación normal
			await procesarCierre(formattedValues, importeCierre, arqueoTotalActual);
		} catch (error) {
			console.error("Error al guardar cierre de caja:", error);
			setError(error.message || "Error al procesar el cierre de caja");
		} finally {
			setLoading(false);
		}
	};

	// Función separada para procesar el cierre después de las validaciones
	const procesarCierre = async (formattedValues, importeCierre, arqueoTotalActual) => {
		try {
			const caja = await getCajaByNoOrId(aperturaActiva.Caja);
			const tpv = await getTPVByNoOrId(caja.TPV);
			setCajaInfo(caja);
			setTpvInfo(tpv);
			const margenCierre = parseFloat(tpv.margenCierre) || 0;

			if (country?.code === COUNTRY_CODES.ECUADOR) {
				// Comparar en centavos para evitar errores de precisión
				const diferenciaCents = Math.abs(toCents(importeCierre) - toCents(arqueoTotalActual));
				const margenCents = toCents(margenCierre);
				const toleranciaCents = 1; // 1 centavo

				// Solo validar si la diferencia es significativa (mayor que la tolerancia y el margen)
				if (diferenciaCents > Math.max(toleranciaCents, margenCents)) {
					// Mostrar modal de autorización de administrador
					setPendingValues(formattedValues);
					setError(
						`La diferencia entre el importe de cierre ($${importeCierre.toFixed(2)}) y el arqueo total ($${arqueoTotalActual.toFixed(2)}) es de $${fromCents(diferenciaCents).toFixed(2)}, que supera el margen permitido de $${fromCents(margenCents).toFixed(2)} para este TPV. Se requiere autorización de administrador.`
					);
					setAdminAuthModal(true);
					setLoading(false);
					return;
				}
			}
			if (country?.code === COUNTRY_CODES.PARAGUAY) {
				const difArqueoEfectivo = (importeApertura + totalEfectivoSistema) - arqueoEfectivo;
				const totalesPorTipo = calculateTotalsByType();
				const totalTarjeta = totalesPorTipo.totalTarjeta
				const difArqueoTarjeta = totalTarjeta - arqueoTarjeta;
				const difTotal = difArqueoEfectivo + difArqueoTarjeta;

				if (difTotal > 0) {
					// Mostrar modal de autorización de administrador
					setPendingValues(formattedValues);
					setError(
						`Se requiere autorización de administrador al haber una diferencia total de ${formatNumberByCountry(difTotal, country, true)} entre los arqueos de tarjeta/efectivo y el cierre ingresado.`
					);
					setAdminAuthModal(true);
					setLoading(false);
					return;

				}
			}

			// Si no hay problemas de margen, continuar directamente con el cierre
			await continuarConCierre(formattedValues);
		} catch (error) {
			console.error("Error al procesar cierre de caja:", error);
			setError(error.message || "Error al procesar el cierre de caja");
			setLoading(false);
		}
	};

	const detallesColumns = [
		{
			title: "Factura",
			dataIndex: "invoiceNo",
			key: "invoiceNo",
			render: (invoiceNo, record) => {
				// Si tiene No (número de BC), mostrar el No, sino mostrar el ID local
				if (invoiceNo && invoiceNo !== "Desconocido") {
					return invoiceNo;
				} else if (record.invoiceId) {
					return `ID: ${record.invoiceId}`;
				}
				return "-";
			},
		},
		// {
		// 	title: "ID Cierre",
		// 	dataIndex: "idCierre",
		// 	key: "idCierre",
		// 	render: (idCierre) => idCierre || "Pendiente",
		// },
		{
			title: "ID Apertura",
			dataIndex: "idApertura",
			key: "idApertura",
		},
		{
			title: "Forma de Pago",
			dataIndex: "DesFormaPago",
			key: "DesFormaPago",
		},
		{
			title: "Tipo",
			dataIndex: "TipoPago",
			key: "TipoPago",
			render: (tipoPago) => (
				<Tag color={tipoPago === "Efectivo" ? "green" : "blue"}>
					{tipoPago || "Efectivo"}
				</Tag>
			),
		},
		{
			title: "Monto",
			dataIndex: "MontoPago",
			key: "MontoPago",
			render: (monto) => {
				let validMonto = monto || 0;
				return formatNumberByCountry(validMonto, country, true);
			},
		}
	];

	return (
		<div className="cierreDeCaja-form-container">
			<h2>{isEditing ? "Editar Cierre de Caja" : "Nuevo Cierre de Caja"}</h2>

			{error && (
				<Alert
					message="Error"
					description={error}
					type="error"
					showIcon
					style={{ marginBottom: 16 }}
					closable
					onClose={() => setError("")}
				/>
			)}

			<Form
				form={form}
				layout="vertical"
				onFinish={handleSubmit}
				disabled={!isEditing && !aperturaActiva}
			>
				<div className="cierreDeCaja-header-section">
					<h3>Información general</h3>
					<Row gutter={16}>
						<Col xs={24} sm={8}>
							<Form.Item
								name="Cajero"
								label="Cajero"
								rules={[{ required: true, message: "Seleccione un cajero" }]}
							>
								<Select
									placeholder="Seleccione un cajero"
									disabled={cierre?.No || (!isEditing && aperturaActiva)}
									showSearch
									optionFilterProp="children"
									filterOption={(input, option) =>
										(option?.label || "")
											.toLowerCase()
											.includes(input.toLowerCase())
									}
								>
									{users.map((user) => (
										<Select.Option key={user.id} value={user.No}>
											{user.firstName} {user.lastName}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</Col>
						<Col xs={24} sm={8}>
							<Form.Item
								name="ImporteDeApertura"
								label="Importe de Apertura"
								rules={[
									{ required: true, message: "Ingrese el importe de apertura" },
								]}
							>
								<InputNumber
									style={{ width: "100%" }}
									disabled={cierre?.No || (!isEditing && aperturaActiva)}
									formatter={(value) => formatNumberByCountry(value, country, true)}
									parser={(value) => parseNumberByCountry(value, country)}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={8}>
							<Form.Item
								name="FechaDeApertura"
								label="Fecha de Apertura"
								rules={[
									{
										required: true,
										message: "Seleccione la fecha de apertura",
									},
								]}
							>
								<DatePicker
									style={{ width: "100%" }}
									format="YYYY-MM-DD"
									disabled={!cierreHistorico && (cierre?.No || (!isEditing && aperturaActiva))}
									onChange={(date) => {
										if (cierreHistorico && date) {
											// Ajustar automáticamente fecha de cierre a última hora
											form.setFieldsValue({
												FechaDeCierre: date.endOf("day"),
											});
										}
									}}
								/>
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col xs={24} sm={8}>
							<Form.Item
								name="Estado"
								label="Estado"
								rules={[{ required: true, message: "Seleccione el estado" }]}
							>
								<Select disabled>
									<Option value="Abierto">Abierto</Option>
									<Option value="Cerrado">Cerrado</Option>
								</Select>
							</Form.Item>
						</Col>
						<Col xs={24} sm={8}>
							<Form.Item name="FechaDeCierre" label="Fecha de Cierre">
								<DatePicker disabled style={{ width: "100%" }} />
							</Form.Item>
						</Col>
						<Col xs={24} sm={8}>
							{/* Campo oculto para guardar el valor real del idApertura */}
							<Form.Item name="idApertura" hidden={true} />
							<Form.Item label="Apertura Relacionada" required={true}>
								{aperturaActiva ? (
									<Input
										value={aperturaTexto}
										disabled={true}
										addonBefore={<BankOutlined />}
										suffix={<Tag color="success">Activa</Tag>}
									/>
								) : (
									<Input
										placeholder="No hay apertura activa"
										disabled={true}
										addonBefore={<BankOutlined />}
										suffix={<Tag color="error">Sin apertura</Tag>}
									/>
								)}
							</Form.Item>
						</Col>
					</Row>

					{/* Checkbox de cierre histórico */}
					<Row gutter={16}>
						<Col span={24}>
							<Form.Item name="CierreHistorico" valuePropName="checked">
								<Checkbox onChange={(e) => setCierreHistorico(e.target.checked)}>
									Cierre de caja histórico
								</Checkbox>
							</Form.Item>
						</Col>
					</Row>

					<div className="cierre-section">
						<h3>Montos de cierre</h3>
						<Row gutter={16}>
							<Col xs={24} sm={8}>
								<div className="currency-field">
									<Form.Item
										label={`Importe Cierre Efectivo (${country?.currency})`}
										tooltip="Calculado automáticamente sumando los pagos en efectivo"
									>
										<InputNumber
											style={{ width: "100%" }}
											precision={2}
											disabled={true}
											value={calculateTotalsByType().totalEfectivo}
											formatter={(value) => formatNumberByCountry(value, country, true)}
											parser={(value) => parseNumberByCountry(value, country)}
											placeholder="Calculado automáticamente"
										/>
									</Form.Item>
								</div>
							</Col>
							<Col xs={24} sm={8}>
								<div className="currency-field">
									<Form.Item
										label={`Importe Cierre Tarjeta (${country?.currency})`}
										tooltip="Calculado automáticamente sumando los pagos con tarjeta"
									>
										<InputNumber
											style={{ width: "100%" }}
											precision={2}
											disabled={true}
											value={calculateTotalsByType().totalTarjeta}
											formatter={(value) => formatNumberByCountry(value, country, true)}
											parser={(value) => parseNumberByCountry(value, country)}
											placeholder="Calculado automáticamente"
										/>
									</Form.Item>
								</div>
							</Col>
							<Col xs={24} sm={8}>
								<div className="currency-field">
									<Form.Item
										name="ImporteDeCierreUS"
										label={`Total Importe Cierre (${country?.currency})`}
										tooltip="Total calculado automáticamente sumando efectivo y tarjeta"
									>
										<InputNumber
											style={{ width: "100%", fontWeight: "bold" }}
											precision={2}
											disabled={true}
											formatter={(value) => formatNumberByCountry(value, country, true)}
											parser={(value) => parseNumberByCountry(value, country)}
											placeholder="Calculado automáticamente"
										/>
									</Form.Item>
								</div>
							</Col>
						</Row>
					</div>

					{
						/*	<div className="aditional-section">
								<h3>Información adicional</h3>
								<Row gutter={16}>
									<Col xs={24} sm={12}>
										<Form.Item
											name="Comprobante_Tarjetas"
											label="Valor total de Tarjetas"
											tooltip="Valor total de transacciones con tarjetas"
										>
											<Input
												placeholder="Ingrese el importe"
												disabled={cierre?.No}
												maxLength={50}
											/>
										</Form.Item>
									</Col>
								</Row>
							</div>*/
					}

					<div className="cierre-section">
						<h3>Arqueo de caja</h3>
						<Row gutter={16}>
							<Col xs={24} sm={8}>
								<Form.Item
									name="ArqueoEfectivoUS"
									label={`Arqueo Efectivo (${country?.currency})`}
									tooltip="Utilice el botón de calculadora para calcular el arqueo por denominaciones"
									rules={[
										{
											validator: (_, value) => {
												// Calcular totales por tipo de pago
												const totalesPorTipo = calculateTotalsByType();
												const totalEfectivo = totalesPorTipo.totalEfectivo || 0;

												// Solo es obligatorio si hubo ventas en efectivo
												if (totalEfectivo > 0) {
													// Usar el valor del estado si está disponible, sino el valor del formulario
													const valorActual = arqueoEfectivoUS >= 0 ? arqueoEfectivoUS : (value || 0);

													if (valorActual >= 0) {
														return Promise.resolve();
													}
													return Promise.reject(
														new Error("El arqueo de efectivo es obligatorio cuando hay ventas en efectivo")
													);
												}

												// Si no hay ventas en efectivo, el campo no es obligatorio
												return Promise.resolve();
											},
										},
									]}
								>
									<DenominacionesCalculator
										value={arqueoEfectivoUS}
										onChange={handleArqueoEfectivoUSChange}
										modalTitle="Arqueo Efectivo - Cálculo por Denominaciones"
										placeholder={`Arqueo Efectivo en (${country?.currency})`}
										disabled={cierre?.No}
										useDefaultValues={false}
									/>
								</Form.Item>
							</Col>
							<Col xs={24} sm={8}>
								<Form.Item
									name="ArqueoTarjetaUS"
									label={`Arqueo Tarjeta (${country?.currency})`}
									tooltip="Ingrese el monto total del arqueo de tarjetas"
									rules={[
										{
											validator: (_, value) => {
												// Calcular totales por tipo de pago
												const totalesPorTipo = calculateTotalsByType();
												const totalTarjeta = totalesPorTipo.totalTarjeta || 0;

												// Solo es obligatorio si hubo ventas con tarjeta
												if (totalTarjeta > 0) {
													// Usar el valor del estado si está disponible, sino el valor del formulario
													const valorActual = arqueoTarjetaUS >= 0 ? arqueoTarjetaUS : (value || 0);

													if (valorActual >= 0) {
														return Promise.resolve();
													}
													return Promise.reject(
														new Error("El arqueo de tarjeta es obligatorio cuando hay ventas con tarjeta")
													);
												}

												// Si no hay ventas con tarjeta, el campo no es obligatorio
												return Promise.resolve();
											},
										},
									]}
								>
									<InputNumber
										style={{ width: "100%" }}
										precision={2}
										value={arqueoTarjetaUS}
										onChange={handleArqueoTarjetaUSChange}
										placeholder={`Arqueo Tarjeta en ${country?.currency}`}
										disabled={cierre?.No}
										min={0}
									/>
								</Form.Item>
							</Col>
							{/* Total Arqueo (Efectivo + Tarjeta) */}
							{country?.code === COUNTRY_CODES.PARAGUAY &&
								<Col xs={24} sm={8}>
									<Form.Item
										label={`Total Arqueo (${country?.currency})`}
										tooltip="Suma del arqueo en efectivo y el arqueo de tarjeta"
									>
										<InputNumber
											style={{ width: "100%" }}
											value={formatNumberByCountry((arqueoEfectivoUS || 0) + (arqueoTarjetaUS || 0), country, true)}
											readOnly
											disabled
										/>
									</Form.Item>
								</Col>
							}
						</Row>
					</div>
					{country?.code === COUNTRY_CODES.PARAGUAY &&
						<div className="cierre-section">
							<h3>Diferencia efectivo</h3>

							<Row gutter={16}>

								{/* Total efectivo esperado */}
								<Col xs={24} sm={8}>
									<Form.Item
										label="Total efectivo"
										tooltip={`Importe apertura (${formatNumberByCountry(importeApertura, country, true)}) + Cierre Efectivo (${formatNumberByCountry(totalEfectivoSistema, country, true)})`}
									>
										<InputNumber
											disabled
											style={{ width: "100%" }}
											precision={2}
											value={totalEsperadoEfectivo}
											formatter={(v) =>
												formatNumberByCountry(v, country, true)
											}
										/>
									</Form.Item>
								</Col>

								{/* Diferencia */}
								<Col xs={24} sm={8}>
									<Form.Item
										label="Diferencia"
										tooltip="Total efectivo - arqueo efectivo"
									>
										<InputNumber
											disabled
											style={{
												width: "100%",
												color: diferencia === 0 ? "green" : "red",
												fontWeight: "bold",
											}}
											precision={2}
											value={diferencia}
											formatter={(v) =>
												formatNumberByCountry(v, country, true)
											}
										/>
									</Form.Item>
								</Col>
							</Row>

						</div>
					}
				</div>

				<div className="cierreDeCaja-detalle-section">
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginBottom: 16,
						}}
					>
						<h3>Detalles del cierre</h3>
					</div>

					<Table
						columns={detallesColumns}
						dataSource={pagosPorFecha.map((pago, index) => ({
							id: `pago-${index}`,
							idCierre: cierre?.No || "Pendiente",
							idApertura: form.getFieldValue("idApertura"),
							DesFormaPago: pago.Payment_Method,
							TipoPago: pago.Tipo_Pago || "Efectivo",
							MontoPago: pago.Amount,
							invoiceNo: pago.invoiceNo,
							invoiceId: pago.invoiceId,
							temp: true,
						}))}
						rowKey="id"
						pagination={{
							...pagination,
							showSizeChanger: true,
							pageSizeOptions: ["5", "10", "20", "50"],
						}}
						onChange={(newPagination) => setPagination(newPagination)}
						loading={loading}
						className="cierreDeCaja-detalle-table"
					/>

					{pagosPorFecha.length > 0 && (
						<div className="cierreDeCaja-totals">
							{/* Sección de Facturas */}
							<div className="cierreDeCaja-totals-section">
								<div className="cierreDeCaja-totals-section-title">
									<strong>FACTURAS</strong>
								</div>
								<div className="cierreDeCaja-totals-item">
									<span>
										<Tag color="green">Efectivo</Tag>
									</span>
									<span>
										{formatNumberByCountry(calculateTotalsByType().facturasEfectivo, country, true)}
									</span>
								</div>
								<div className="cierreDeCaja-totals-item">
									<span>
										<Tag color="blue">Tarjeta</Tag>
									</span>
									<span>
										{formatNumberByCountry(calculateTotalsByType().facturasTarjeta, country, true)}
									</span>
								</div>
								<div className="cierreDeCaja-totals-item subtotal">
									<span>Subtotal Facturas:</span>
									<span>
										{
											formatNumberByCountry(
												(
													calculateTotalsByType().facturasEfectivo +
													calculateTotalsByType().facturasTarjeta
												),
												country,
												true)
										}
									</span>
								</div>
							</div>

							{/* Sección de Notas de Crédito (solo si hay) */}
							{(calculateTotalsByType().notasCreditoEfectivo > 0 ||
								calculateTotalsByType().notasCreditoTarjeta > 0) && (
									<div className="cierreDeCaja-totals-section">
										<div className="cierreDeCaja-totals-section-title">
											<strong>NOTAS DE CRÉDITO</strong>
										</div>
										<div className="cierreDeCaja-totals-item">
											<span>
												<Tag color="green">Efectivo</Tag>
											</span>
											<span>
												{formatNumberByCountry(-(calculateTotalsByType().notasCreditoEfectivo), country, true)}
											</span>
										</div>
										<div className="cierreDeCaja-totals-item">
											<span>
												<Tag color="blue">Tarjeta</Tag>
											</span>
											<span>
												{formatNumberByCountry(-(calculateTotalsByType().notasCreditoTarjeta), country, true)}

											</span>
										</div>
										<div className="cierreDeCaja-totals-item subtotal">
											<span>Subtotal Notas Crédito:</span>
											<span>
												{formatNumberByCountry(-(
													calculateTotalsByType().notasCreditoEfectivo +
													calculateTotalsByType().notasCreditoTarjeta
												)
													, country
													, true)
												}
											</span>
										</div>
									</div>
								)}

							{/* Totales Finales */}
							<div className="cierreDeCaja-totals-section">
								<div className="cierreDeCaja-totals-section-title">
									<strong>TOTALES FINALES</strong>
								</div>
								<div className="cierreDeCaja-totals-item">
									<span>
										<Tag color="green">Total Efectivo</Tag>
									</span>
									<span>
										{formatNumberByCountry(calculateTotalsByType().totalEfectivo, country, true)}
									</span>
								</div>
								<div className="cierreDeCaja-totals-item">
									<span>
										<Tag color="blue">Total Tarjeta</Tag>
									</span>
									<span>
										{formatNumberByCountry(calculateTotalsByType().totalTarjeta, country, true)}
									</span>
								</div>
								<div className="cierreDeCaja-totals-item grand-total">
									<span>TOTAL GENERAL:</span>
									<span>{formatNumberByCountry(calculateTotalsByType().total, country, true)} </span>
								</div>
							</div>
						</div>
					)}
				</div>

				<div className="cierreDeCaja-form-actions">
					<Button onClick={onCancel}>Cancelar</Button>
					<Button
						type="primary"
						htmlType="submit"
						loading={loading}
						disabled={cierre?.No || (!isEditing && !aperturaActiva)}
					>
						{saveButtonText}
					</Button>
				</div>
			</Form>

			{/* Modal de autorización de administrador */}
			<Modal
				title={
					<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
						<LockOutlined />
						<span>Autorización de Administrador</span>
					</div>
				}
				open={adminAuthModal}
				onCancel={handleCancelAdminAuth}
				footer={null}
				centered
				maskClosable={false}
			>
				<div style={{ marginBottom: "16px" }}>

					<Alert
						message="Autorización requerida"
						description={error ?? "La diferencia entre el importe de cierre y el arqueo supera el margen permitido. Se requiere autorización de administrador para continuar."}
						type="warning"
						showIcon
					/>
				</div>

				<Form form={adminForm} layout="vertical" onFinish={handleAdminAuth}>
					<Form.Item
						name="username"
						label="Usuario"
						rules={[
							{
								required: true,
								message: "Ingrese el usuario de administrador",
							},
						]}
					>
						<Input
							prefix={<UserOutlined />}
							placeholder="Usuario de administrador"
							autoComplete="username"
						/>
					</Form.Item>

					<Form.Item
						name="password"
						label="Contraseña"
						rules={[
							{
								required: true,
								message: "Ingrese la contraseña de administrador",
							},
						]}
					>
						<Input.Password
							prefix={<LockOutlined />}
							placeholder="Contraseña de administrador"
							autoComplete="current-password"
						/>
					</Form.Item>

					<div style={{ textAlign: "right", marginTop: "24px" }}>
						<Button
							onClick={handleCancelAdminAuth}
							style={{ marginRight: "8px" }}
						>
							Cancelar
						</Button>
						<Button type="primary" htmlType="submit" loading={adminAuthLoading}>
							Autorizar
						</Button>
					</div>
				</Form>
			</Modal>

			{/* Modal de sincronización */}
			<Modal
				title={
					<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
						<CloudSyncOutlined />
						<span>Sincronizando</span>
					</div>
				}
				open={syncingModal}
				footer={null}
				closable={false}
				maskClosable={false}
				centered
			>
				<div style={{ textAlign: "center", padding: "20px" }}>
					<Spin size="large" />
					<div style={{ marginTop: "16px", fontSize: "16px" }}>
						{syncingStatus || "Sincronizando datos..."}
					</div>
				</div>
			</Modal>

			{/* Modal del ticket de cierre */}
			{savedCierreForTicket && (
				<CierreTicket
					open={ticketVisible}
					onClose={handleTicketClose}
					cierre={savedCierreForTicket}
					detalles={savedCierreForTicket.detalles || []}
					caja={cajaInfo?.No || cajaInfo?.id}
					tpv={tpvInfo?.No || tpvInfo?.id}
				/>
			)}

			{/* Modal de confirmación para cierre en $0.00 */}
			<Modal
				title="¿Está seguro de cerrar la caja en $0.00?"
				open={confirmCierreEnCeroModal}
				onOk={handleConfirmCierreEnCero}
				onCancel={handleCancelCierreEnCero}
				okText="Sí, cerrar en $0.00"
				cancelText="Cancelar"
				centered
				maskClosable={false}
			>
				<Alert
					message="Atención"
					description="Tanto el importe de cierre como el arqueo son $0.00. ¿Está seguro de que desea cerrar la caja sin dinero?"
					type="warning"
					showIcon
					style={{ marginBottom: 16 }}
				/>
				<p>Esta acción cerrará la caja con:</p>
				<ul>
					<li><strong>Importe de cierre:</strong> $0.00</li>
					<li><strong>Arqueo:</strong> $0.00</li>
				</ul>
			</Modal>
		</div>
	);
};

export default CierreDeCajaForm;
